import { useState } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import FormInput from '../common/FormInput';
import Sidebar from '../common/Sidebar';
import UserAvatar from '../common/UserAvatar';

function Profile() {
  const { user, updateProfile, updatePreferences } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    mobile: user?.mobile || '',
    addressLine1: user?.address?.line1 || '',
    addressLine2: user?.address?.line2 || '',
    addressLine3: user?.address?.line3 || '',
    town: user?.address?.town || '',
    county: user?.address?.county || '',
    postcode: user?.address?.postcode || '',
    marketingEmails: user?.preferences?.marketingEmails ?? true,
    magazineEmails: user?.preferences?.magazineEmails ?? true,
    renewalEmails: user?.preferences?.renewalEmails ?? true,
    printedMagazine: user?.preferences?.printedMagazine ?? false,
  });
  const [errors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleBlur = e => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Update profile data
      await updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        telephone: formData.telephone,
        mobile: formData.mobile,
        address: {
          line1: formData.addressLine1,
          line2: formData.addressLine2,
          line3: formData.addressLine3,
          town: formData.town,
          county: formData.county,
          postcode: formData.postcode,
        },
      });

      // Update preferences
      await updatePreferences({
        marketingEmails: formData.marketingEmails,
        magazineEmails: formData.magazineEmails,
        renewalEmails: formData.renewalEmails,
        printedMagazine: formData.printedMagazine,
      });
    } catch (error) {
      setSubmitError('Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const gravatarProfileUrl = 'https://gravatar.com/connect/';

  return (
    <div className="min-h-screen bg-hdlcc-black">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64">
          <div className="p-8">
            <div className="bg-gradient-to-br from-hdlcc-darkGray via-hdlcc-darkGray to-hdlcc-mediumGray text-white p-8 rounded-2xl shadow-elevation-2 border border-hdlcc-mediumGray/30">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-hdlcc-textPrimary tracking-tight">
                    My Profile
                  </h2>
                  <p className="text-hdlcc-textSecondary mt-1">
                    Manage your profile and preferences
                  </p>
                </div>
              </div>

              {/* Profile Picture Section */}
              <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 mb-8">
                <div className="flex items-center space-x-6">
                  <UserAvatar email={user?.email} size={80} />
                  <div>
                    <h3 className="text-lg font-bold text-hdlcc-textPrimary mb-2">
                      Profile Picture
                    </h3>
                    <p className="text-hdlcc-textSecondary mb-3">
                      Your profile picture is provided by Gravatar
                    </p>
                    <a
                      href={gravatarProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hdlcc-primary hover:text-hdlcc-primaryLight font-medium transition-colors"
                    >
                      Update on Gravatar.com →
                    </a>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="bg-hdlcc-error/10 border border-hdlcc-error/30 text-hdlcc-error p-6 rounded-xl mb-8 shadow-elevation-1">
                  <p className="font-medium">{submitError}</p>
                </div>
              )}

              {/* Personal Information Form */}
              <form onSubmit={handleSubmit}>
                <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 mb-8">
                  <h3 className="text-xl font-bold mb-6 text-hdlcc-textPrimary">
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.firstName}
                      touched={touched.firstName}
                      required
                    />

                    <FormInput
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.lastName}
                      touched={touched.lastName}
                      required
                    />

                    <FormInput
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.email}
                      touched={touched.email}
                      required
                    />

                    <FormInput
                      label="Telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.telephone}
                      touched={touched.telephone}
                    />

                    <FormInput
                      label="Mobile"
                      name="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.mobile}
                      touched={touched.mobile}
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 mb-8">
                  <h3 className="text-xl font-bold mb-6 text-hdlcc-textPrimary">Address</h3>
                  <div className="space-y-4">
                    <FormInput
                      label="Address Line 1"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.addressLine1}
                      touched={touched.addressLine1}
                      required
                    />

                    <FormInput
                      label="Address Line 2"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.addressLine2}
                      touched={touched.addressLine2}
                    />

                    <FormInput
                      label="Address Line 3"
                      name="addressLine3"
                      value={formData.addressLine3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.addressLine3}
                      touched={touched.addressLine3}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormInput
                        label="Town/City"
                        name="town"
                        value={formData.town}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.town}
                        touched={touched.town}
                        required
                      />

                      <FormInput
                        label="County"
                        name="county"
                        value={formData.county}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.county}
                        touched={touched.county}
                      />
                    </div>

                    <FormInput
                      label="Postcode"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.postcode}
                      touched={touched.postcode}
                      required
                    />
                  </div>
                </div>

                {/* Contact Preferences */}
                <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1">
                  <h3 className="text-xl font-bold mb-6 text-hdlcc-textPrimary">
                    Contact Preferences
                  </h3>
                  <div className="space-y-5">
                    <div className="flex items-start space-x-4 p-4 bg-hdlcc-gray/40 rounded-lg border border-hdlcc-lightGray/10">
                      <input
                        id="renewalEmails"
                        type="checkbox"
                        name="renewalEmails"
                        checked={formData.renewalEmails}
                        onChange={handleChange}
                        className="form-checkbox text-hdlcc-primary h-5 w-5 rounded bg-hdlcc-darkGray border-hdlcc-lightGray mt-0.5"
                      />
                      <label htmlFor="renewalEmails" className="flex-1 cursor-pointer">
                        <span className="text-hdlcc-textPrimary font-semibold block mb-1">
                          Renewal Reminder Notifications
                        </span>
                        <p className="text-hdlcc-textTertiary text-sm leading-relaxed">
                          Receive automated renewal reminders at 60, 30, 14, and 7 days before
                          expiry
                        </p>
                      </label>
                    </div>

                    <div className="flex items-start space-x-4 p-4 bg-hdlcc-gray/40 rounded-lg border border-hdlcc-lightGray/10">
                      <input
                        id="magazineEmails"
                        type="checkbox"
                        name="magazineEmails"
                        checked={formData.magazineEmails}
                        onChange={handleChange}
                        className="form-checkbox text-hdlcc-primary h-5 w-5 rounded bg-hdlcc-darkGray border-hdlcc-lightGray mt-0.5"
                      />
                      <label htmlFor="magazineEmails" className="flex-1 cursor-pointer">
                        <span className="text-hdlcc-textPrimary font-semibold block mb-1">
                          Digital Magazine
                        </span>
                        <p className="text-hdlcc-textTertiary text-sm leading-relaxed">
                          Receive notifications when new quarterly magazine issues are available
                          online
                        </p>
                      </label>
                    </div>

                    <div className="flex items-start space-x-4 p-4 bg-hdlcc-gray/40 rounded-lg border border-hdlcc-lightGray/10">
                      <input
                        id="printedMagazine"
                        type="checkbox"
                        name="printedMagazine"
                        checked={formData.printedMagazine}
                        onChange={handleChange}
                        className="form-checkbox text-hdlcc-primary h-5 w-5 rounded bg-hdlcc-darkGray border-hdlcc-lightGray mt-0.5"
                      />
                      <label htmlFor="printedMagazine" className="flex-1 cursor-pointer">
                        <span className="text-hdlcc-textPrimary font-semibold block mb-1">
                          Printed Magazine by Post
                        </span>
                        <p className="text-hdlcc-textTertiary text-sm leading-relaxed">
                          Request a printed copy of the quarterly magazine to be posted to your
                          address
                        </p>
                      </label>
                    </div>

                    <div className="flex items-start space-x-4 p-4 bg-hdlcc-gray/40 rounded-lg border border-hdlcc-lightGray/10">
                      <input
                        id="marketingEmails"
                        type="checkbox"
                        name="marketingEmails"
                        checked={formData.marketingEmails}
                        onChange={handleChange}
                        className="form-checkbox text-hdlcc-primary h-5 w-5 rounded bg-hdlcc-darkGray border-hdlcc-lightGray mt-0.5"
                      />
                      <label htmlFor="marketingEmails" className="flex-1 cursor-pointer">
                        <span className="text-hdlcc-textPrimary font-semibold block mb-1">
                          Club News and Events
                        </span>
                        <p className="text-hdlcc-textTertiary text-sm leading-relaxed">
                          Receive emails about club events, news, and other marketing communications
                        </p>
                      </label>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end mt-8 pt-6 border-t border-hdlcc-lightGray/20">
                      <button
                        type="submit"
                        className="px-8 py-3 text-white bg-gradient-to-r from-hdlcc-primary to-hdlcc-primaryLight rounded-lg hover:shadow-glow-primary disabled:opacity-50 font-semibold transition-all duration-200"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
